/**
 * Speaking-assessment façade — historically named "pronunciation
 * service" because it wrapped Azure Pronunciation Assessment. As of
 * the v2 cutover it forwards to /api/v1/speaking/assess (Groq +
 * Deepgram + WebAudio + Gemini) and adapts the response back to the
 * legacy PronunciationResult shape so the shadowing / vocab-book
 * surfaces don't need a coordinated rewrite.
 *
 * New v2-only fields (coach, prosody, asrProvider, ...) are surfaced
 * alongside the legacy ones — call sites that want them just read
 * the extra properties; sites that don't are unaffected.
 */
import i18n from 'i18next';
import {
    speakingAssessmentService,
    SpeakingAssessmentResponse,
    CoachResult,
    ProsodySummary,
    AssessmentWord,
} from './speakingAssessmentService';

/**
 * Resolve the learner's preferred coaching language. Order:
 *   1) explicit option from caller
 *   2) i18next current language (set by the language switcher)
 *   3) localStorage userLocale (set by some onboarding flows)
 *   4) browser language
 *   5) zh-TW fallback (primary market)
 */
const resolveLocale = (override?: string): string => {
    if (override) return override;
    if (i18n.language) return i18n.language;
    const stored = (typeof window !== 'undefined') && window.localStorage?.getItem('userLocale');
    if (stored) return stored;
    if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
    return 'zh-TW';
};

export interface PhonemeScore {
    phoneme: string;
    accuracyScore: number;
}

export interface WordScore {
    word: string;
    accuracyScore: number;
    errorType: string;
    phonemes?: PhonemeScore[];
    suggestion?: string;
}

export interface PronunciationResult {
    overallScore: number;
    accuracyScore: number;
    fluencyScore: number;
    completenessScore: number;
    pronunciationScore: number;
    prosodyScore: number;
    words: WordScore[];
    recognizedText: string;
    feedback: string;
    /** v2 extras — undefined on legacy responses (none in production
     *  after this cutover). Surfaced so call sites that want the
     *  Loopy coach panel + pitch contour can opt in. */
    coach?: CoachResult;
    prosody?: ProsodySummary;
    /** Surface what produced this result so the UI can show
     *  "Powered by ..." or telemetry can split v1 / v2 retention. */
    asrProvider?: string;
}

export interface PronunciationStatus {
    configured: boolean;
    message: string;
}

const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

const adaptWord = (w: AssessmentWord): WordScore => ({
    word: w.word,
    accuracyScore: w.accuracyScore,
    errorType: w.errorType,
    suggestion: w.suggestion,
    // phonemes intentionally omitted — v2 doesn't produce them.
});

const adaptResponse = (r: SpeakingAssessmentResponse): PronunciationResult => ({
    overallScore: r.scores.overall,
    accuracyScore: r.scores.accuracy,
    fluencyScore: r.scores.fluency,
    completenessScore: r.scores.completeness,
    // Azure had a separate "pronunciation score" axis that overlapped
    // with accuracy + completeness; v2 doesn't model it. Keep the
    // field for API compat by reusing accuracy.
    pronunciationScore: r.scores.accuracy,
    prosodyScore: r.scores.prosody,
    words: r.words.map(adaptWord),
    recognizedText: r.transcript,
    // Coach handles user-facing feedback now; the legacy `feedback`
    // string was a single static blurb derived from overallScore.
    // Coach is optional after the assess/coach split — empty string
    // when absent, the host page fetches and renders coach
    // separately via the dedicated /speaking/coach call.
    feedback: r.coach?.encouragement ?? '',
    coach: r.coach,
    prosody: r.prosody,
    asrProvider: r.asrProvider,
});

class PronunciationService {
    async getStatus(): Promise<PronunciationStatus> {
        // The v2 endpoint doesn't have a configured/message status —
        // it just runs. Return a stub so existing UI gating logic
        // (e.g. "is the service available?") keeps working.
        return { configured: true, message: 'speaking-assessment v2' };
    }

    /**
     * Run the assess + coach pair as a progressive sequence:
     *
     *   1. Wait for /speaking/assess (~2 s with Deepgram primary).
     *      Returns scores + transcript + prosody + issues. The
     *      caller can render the score panel as soon as this
     *      promise resolves.
     *   2. If `onCoachReady` is provided, fire /speaking/coach in
     *      the background and invoke the callback when Gemini
     *      replies (~2-3 s later). The host page slots the coach
     *      result into its state and re-renders just the
     *      <CoachingPanel>. This keeps "正在分析發音" feedback
     *      fast (~2 s) instead of waiting another 2-3 s for
     *      Gemini before showing anything.
     *
     * Errors in the background coach fetch are logged but never
     * surfaced — the score panel is the contract; the coach is a
     * progressive enhancement.
     */
    async assessPronunciation(
        audioBlob: Blob,
        referenceText: string,
        options: {
            locale?: string;
            onCoachReady?: (coach: CoachResult) => void;
        } = {},
    ): Promise<PronunciationResult> {
        const locale = resolveLocale(options.locale);
        const audioBase64 = await blobToBase64(audioBlob);
        const res = await speakingAssessmentService.assess({
            audioBase64,
            mimeType: audioBlob.type || 'audio/webm',
            referenceText,
            locale,
        });

        if (options.onCoachReady) {
            // Pass the issues + prosody + per-word breakdown back to
            // the server's /coach endpoint so Gemini gets to calibrate
            // tone (overallScore) and avoid complimenting flagged
            // words (words[].errorType).
            speakingAssessmentService.generateCoach({
                referenceText,
                transcript: res.transcript,
                locale,
                overallScore: res.scores.overall,
                prosody: {
                    score: res.prosody.score,
                    pitchRangeSt: res.prosody.pitchRangeSt,
                    pitchVarianceSt: res.prosody.pitchVarianceSt,
                    voicedRatio: res.prosody.voicedRatio,
                },
                issues: res.issues,
                words: res.words.map(w => ({ word: w.word, errorType: w.errorType })),
            })
                .then(options.onCoachReady)
                .catch(err => console.warn('[pronunciation] coach fetch failed', err));
        }

        return adaptResponse(res);
    }

    /** Legacy multipart entry point. The v2 endpoint takes base64
     *  only — for any caller still using this we round-trip through
     *  base64. Removed in PR 4 once all call sites migrate. */
    assessPronunciationWithFile(
        audioBlob: Blob,
        referenceText: string,
    ): Promise<PronunciationResult> {
        return this.assessPronunciation(audioBlob, referenceText);
    }
}

export const pronunciationService = new PronunciationService();
